import React, { useState } from 'react'
import { Icons } from '@pankod/refine-antd'
import { useCan } from '@pankod/refine-core'

import DrawerBuktiKaryaConfig from '../../../../program-setup/blocks/DrawerBuktiKaryaConfig'
import ProgramImportConfigDrawer, {
  useProgramImportConfigDrawer,
} from '../../../../program-setup/blocks/program-import-config/ProgramImportConfigDrawer'
import InstructorDrawer, {
  useInstructorDrawer,
} from '../../../../program-setup/blocks/webinar/InstructorDrawer'
import WebinarDrawer, {
  useWebinarConfigDrawer,
} from '../../../../program-setup/blocks/webinar/WebinarDrawer'
import { PROGRAM_STATE } from '../../../../types'
import { Highlighter } from '../../utils'

import type {
  TConfigurationSection,
  TConfigurationSectionContext,
} from '../types'

export const useProgramToolsConfiguration = ({
  program,
  programId,
  refetchProgramDetail,
  runAndClose,
  search,
}: TConfigurationSectionContext): TConfigurationSection => {
  const [prerequisiteVisible, setPrerequisiteVisible] = useState(false)
  const { show: showProgramImport, ...programImportDrawerProps } =
    useProgramImportConfigDrawer()
  const { show: showWebinar, ...webinarDrawerProps } = useWebinarConfigDrawer()
  const { show: showInstructor, ...instructorDrawerProps } =
    useInstructorDrawer()
  const { data: canImportProgram } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'copy-config' },
  })
  const { data: canAccessWebinar } = useCan({
    action: 'show',
    resource: 'lms-webinar-management',
  })

  return {
    menuItems: [
      ...(canImportProgram?.can &&
      [PROGRAM_STATE.PREPARATION, PROGRAM_STATE.RUNNING].includes(
        program?.status!,
      )
        ? [
            {
              type: 'group' as const,
              label: <Highlighter search={search} text="Impor Program" />,
              key: 'Impor Program',
              children: [
                {
                  icon: <Icons.CopyOutlined />,
                  label: (
                    <Highlighter
                      search={search}
                      text="Impor Konfigurasi Program"
                    />
                  ),
                  key: 'Impor Konfigurasi Program',
                  onClick: () =>
                    runAndClose(() =>
                      showProgramImport(programId, program?.name!),
                    ),
                },
              ],
            },
          ]
        : []),
      {
        type: 'group',
        label: <Highlighter search={search} text="Prasyarat Sertifikat" />,
        key: 'Prasyarat Sertifikat',
        children: [
          {
            icon: <Icons.SettingOutlined />,
            label: (
              <Highlighter
                search={search}
                text="Konfigurasi Prasyarat Sertifikat"
              />
            ),
            key: 'Konfigurasi Prasyarat Sertifikat',
            onClick: () => runAndClose(() => setPrerequisiteVisible(true)),
          },
        ],
      },
      ...(canAccessWebinar?.can
        ? [
            {
              type: 'group' as const,
              label: <Highlighter search={search} text="Webinar" />,
              key: 'Webinar',
              children: [
                {
                  icon: <Icons.VideoCameraOutlined />,
                  label: <Highlighter search={search} text="Kelola Webinar" />,
                  key: 'Kelola Webinar',
                  onClick: () => runAndClose(() => showWebinar(programId)),
                },
                {
                  icon: <Icons.UserOutlined />,
                  label: (
                    <Highlighter search={search} text="Daftar Instruktur" />
                  ),
                  key: 'Daftar Instruktur',
                  onClick: () => runAndClose(() => showInstructor(programId)),
                },
              ],
            },
          ]
        : []),
    ],
    overlays: (
      <>
        <ProgramImportConfigDrawer
          {...programImportDrawerProps}
          entityId={program?.entityId}
          programLastConfig={program?.programLastConfig}
          refetchProgramDetail={refetchProgramDetail}
        />
        <DrawerBuktiKaryaConfig
          programId={programId}
          show={prerequisiteVisible}
          setShow={setPrerequisiteVisible}
        />
        <WebinarDrawer {...webinarDrawerProps} />
        <InstructorDrawer
          useNewCertificate={program?.useNewCertificate}
          {...instructorDrawerProps}
        />
      </>
    ),
  }
}
